import { useMemo, useState } from 'react';
import { Modal, Button } from '../primitives';
import { useCompanies } from '../../api/hooks';
import type { Company } from '../../api/schemas';
import styles from './CompanyPickerButton.module.css';
import { cx } from '../../lib/cx';

/**
 * Tlačítko „📋 Adresář" — otevře modal se seznamem firem z DB (/api/companies/).
 * Po výběru volá `onPick(company)`. Port funkcionality z frontend/index.html:20640
 * (denikLoadFromAddrBook + denikApplyCompany).
 *
 * Volitelně lze předat `preferRole` — firmy s touto rolí se zobrazí první (neostatní zůstanou
 * přístupné níže, stejně jako ve staré verzi).
 */

export interface CompanyPickResult {
  id: number;
  nazev: string;
  ico: string;
  dic: string;
  adresa: string;
  kontakt_jmeno: string;
  kontakt_tel: string;
  kontakt_email: string;
  stavbyvedouci: string;
  ckait: string;
  bozp_koordinator: string;
  bozp_tel: string;
  role: string;
  fakturacni_adresa: string;
  fakturacni_email: string;
}

export interface CompanyPickerButtonProps {
  onPick: (data: CompanyPickResult) => void;
  /** Role, která se v seznamu seřadí nahoru (např. 'zhotovitel', 'objednatel'). */
  preferRole?: string;
  label?: string;
  compact?: boolean;
  title?: string;
  /** Disabled kdyz není co dělat (např. nenapojená stavba). */
  disabled?: boolean;
}

const ROLE_ICON: Record<string, string> = {
  zhotovitel: '🔧',
  objednatel: '📋',
  investitor: '📋',
  oba: '↔',
  subdodavatel: '👷',
  tds: '🔍',
  projektant: '📐',
  bozp_koordinator: '⛑',
};

function toPickResult(c: Company): CompanyPickResult {
  return {
    id: c.id,
    nazev: c.nazev ?? '',
    ico: c.ico ?? '',
    dic: c.dic ?? '',
    adresa: c.adresa ?? '',
    kontakt_jmeno: c.kontakt_jmeno ?? '',
    kontakt_tel: c.kontakt_tel ?? '',
    kontakt_email: c.kontakt_email ?? '',
    stavbyvedouci: c.stavbyvedouci ?? '',
    ckait: c.ckait ?? '',
    bozp_koordinator: c.bozp_koordinator ?? '',
    bozp_tel: c.bozp_tel ?? '',
    role: c.role ?? '',
    fakturacni_adresa: c.fakturacni_adresa ?? '',
    fakturacni_email: c.fakturacni_email ?? '',
  };
}

export function CompanyPickerButton({
  onPick,
  preferRole,
  label = 'Adresář',
  compact,
  title,
  disabled,
}: CompanyPickerButtonProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const { data: companies = [], isLoading, error } = useCompanies();

  const sorted = useMemo(() => {
    const query = q.trim().toLocaleLowerCase('cs');
    const list = [...companies];
    list.sort((a, b) => {
      const aMatch = preferRole && (a.role === preferRole || a.role === 'oba') ? 0 : 1;
      const bMatch = preferRole && (b.role === preferRole || b.role === 'oba') ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return (a.nazev ?? '').localeCompare(b.nazev ?? '', 'cs');
    });
    if (!query) return list;
    return list.filter((c) => {
      const hay = [c.nazev, c.ico, c.kontakt_jmeno, c.kontakt_email, c.stavbyvedouci]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('cs');
      return hay.includes(query);
    });
  }, [companies, q, preferRole]);

  function choose(c: Company) {
    onPick(toPickResult(c));
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={title ?? 'Načíst firmu z adresáře'}
      >
        <span aria-hidden>📋</span>
        {!compact && <span>{label}</span>}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="lg"
        title={title ?? 'Vyberte firmu z adresáře'}
        footer={
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Zavřít
          </Button>
        }
      >
        <input
          className={styles.search}
          placeholder="Hledat podle názvu, IČO, kontaktu…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />

        {isLoading ? (
          <div className={styles.empty}>Načítám…</div>
        ) : error ? (
          <div className={styles.statusErr}>
            Chyba: {error instanceof Error ? error.message : 'nelze načíst'}
          </div>
        ) : sorted.length === 0 ? (
          <div className={styles.empty}>
            {companies.length === 0
              ? 'Adresář je prázdný. Firmu můžete přidat přes „💾 Uložit do adresáře".'
              : `Žádný výsledek pro „${q.trim()}".`}
          </div>
        ) : (
          <div className={styles.list}>
            {sorted.map((c) => {
              const match = preferRole && (c.role === preferRole || c.role === 'oba');
              const icon = ROLE_ICON[c.role] ?? '';
              return (
                <button key={c.id} type="button" className={styles.item} onClick={() => choose(c)}>
                  <div className={styles.itemMain}>
                    <div className={styles.itemName}>{c.nazev || '— bez názvu —'}</div>
                    <div className={styles.itemMeta}>
                      {c.ico && <span className={styles.mono}>IČO {c.ico}</span>}
                      {c.kontakt_jmeno && <span>{c.kontakt_jmeno}</span>}
                      {c.kontakt_email && <span>{c.kontakt_email}</span>}
                    </div>
                    {c.adresa && <div className={styles.itemContact}>{c.adresa}</div>}
                  </div>
                  <span className={cx(styles.roleTag, match && styles.match)}>
                    {icon} {c.role || '—'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
}
